// Deciding which category an item belongs in.
//
// Two layers:
//   1. `suggestCategory` — a static keyword → category-name map. No ML, no cost.
//   2. `resolveCategoryId` — what the recipe importer actually calls: prefer what
//      *this household* has filed the ingredient under before, and only fall back
//      to the keyword map. That's what makes a wrong guess stay fixed once the
//      user corrects it.

import type { Item, Category } from './supabase'
import { fold, nameKey, sameWords, stemToken, tokens } from './normalize'

// The six categories every household is seeded with. Each has an English
// name (what new households get) and the Swedish name older households were
// seeded with; the keyword map and `suggestCategory` speak in the English
// canonical name, and `categoryIdForSuggestion` accepts either — so a list
// created as "Kylvaror" still receives milk. Named constants so the keyword
// map below stays readable at a glance.
export const PRODUCE   = 'Fruit & veg'
export const DAIRY     = 'Dairy & chilled'
export const BAKERY    = 'Bread & bakery'
export const PANTRY    = 'Pantry'
export const FROZEN    = 'Frozen'
export const HOUSEHOLD = 'Household'

interface CategorySlot {
  name: string
  /** Every name this slot has ever been seeded under, folded. */
  aliases: string[]
  icon: string
}

const SLOTS: CategorySlot[] = [
  { name: PRODUCE,   aliases: ['frukt & gront'],    icon: '🥦' },
  { name: DAIRY,     aliases: ['kylvaror'],         icon: '🥛' },
  { name: BAKERY,    aliases: ['brod & bakverk'],   icon: '🍞' },
  { name: PANTRY,    aliases: ['skafferi'],         icon: '🥫' },
  { name: FROZEN,    aliases: ['fryst'],            icon: '🧊' },
  { name: HOUSEHOLD, aliases: ['hushall & hygien'], icon: '🧹' },
]

/** What a new household is created with: seed_categories() in 049_category_lifecycle.sql (pinned by seedCategories.test.ts). */
export const SEED_CATEGORIES = SLOTS.map(({ name, icon }) => ({ name, icon }))

const MAP: Record<string, string> = {
  // English keywords. Longest-first matching (below) means a specific word
  // wins over a shorter one that's a substring of it ("ice cream" → Frozen
  // before "cream" → Dairy; "chickpea" → Pantry before "chicken" → Dairy).
  milk: DAIRY, cheese: DAIRY, yogurt: DAIRY, yoghurt: DAIRY, butter: DAIRY, cream: DAIRY,
  'sour cream': DAIRY, 'cream cheese': DAIRY, 'cottage cheese': DAIRY, 'creme fraiche': DAIRY,
  egg: DAIRY, tofu: DAIRY, hummus: DAIRY, margarine: DAIRY,
  apple: PRODUCE, banana: PRODUCE, lemon: PRODUCE, lime: PRODUCE, orange: PRODUCE, onion: PRODUCE,
  tomato: PRODUCE, spinach: PRODUCE, lettuce: PRODUCE, carrot: PRODUCE, celery: PRODUCE,
  potato: PRODUCE, garlic: PRODUCE, broccoli: PRODUCE, cauliflower: PRODUCE, avocado: PRODUCE,
  cucumber: PRODUCE, 'bell pepper': PRODUCE, 'red pepper': PRODUCE, 'green pepper': PRODUCE,
  'yellow pepper': PRODUCE, 'sweet pepper': PRODUCE, capsicum: PRODUCE, chilli: PRODUCE, mushroom: PRODUCE,
  eggplant: PRODUCE, aubergine: PRODUCE, butternut: PRODUCE, 'green bean': PRODUCE,
  zucchini: PRODUCE, courgette: PRODUCE, leek: PRODUCE, cabbage: PRODUCE, kale: PRODUCE,
  ginger: PRODUCE, grape: PRODUCE, pear: PRODUCE, berry: PRODUCE, strawberry: PRODUCE,
  blueberry: PRODUCE, raspberry: PRODUCE, mango: PRODUCE, melon: PRODUCE, pineapple: PRODUCE,
  peach: PRODUCE, plum: PRODUCE, kiwi: PRODUCE, herb: PRODUCE, parsley: PRODUCE, basil: PRODUCE,
  coriander: PRODUCE, cilantro: PRODUCE, dill: PRODUCE, mint: PRODUCE, thyme: PRODUCE,
  rosemary: PRODUCE, scallion: PRODUCE, 'spring onion': PRODUCE, shallot: PRODUCE,
  sweetcorn: PRODUCE, 'sweet potato': PRODUCE, fruit: PRODUCE, vegetable: PRODUCE,
  bread: BAKERY, bagel: BAKERY, muffin: BAKERY, roll: BAKERY, bun: BAKERY, croissant: BAKERY,
  baguette: BAKERY, pita: BAKERY, wrap: BAKERY, naan: BAKERY, crumpet: BAKERY, cake: BAKERY,
  donut: BAKERY, doughnut: BAKERY, crispbread: BAKERY,
  chicken: DAIRY, beef: DAIRY, steak: DAIRY, pork: DAIRY, lamb: DAIRY, turkey: DAIRY,
  mince: DAIRY, sausage: DAIRY, bacon: DAIRY, salami: DAIRY,
  'oat milk': DAIRY, 'almond milk': DAIRY, 'soy milk': DAIRY,
  salmon: DAIRY, fish: DAIRY, cod: DAIRY, shrimp: DAIRY, prawn: DAIRY, tuna: DAIRY,
  pizza: FROZEN, 'ice cream': FROZEN, frozen: FROZEN, 'fish finger': FROZEN, 'fish stick': FROZEN,
  juice: PANTRY, 'fruit juice': PANTRY, 'orange juice': PANTRY, 'apple juice': PANTRY,
  water: PANTRY, soda: PANTRY, coffee: PANTRY, tea: PANTRY, beer: PANTRY, wine: PANTRY,
  soap: HOUSEHOLD, shampoo: HOUSEHOLD, conditioner: HOUSEHOLD, detergent: HOUSEHOLD,
  tissue: HOUSEHOLD, 'toilet paper': HOUSEHOLD, 'toilet roll': HOUSEHOLD, 'paper towel': HOUSEHOLD,
  'kitchen roll': HOUSEHOLD, 'dish soap': HOUSEHOLD, 'washing up liquid': HOUSEHOLD,
  toothpaste: HOUSEHOLD, toothbrush: HOUSEHOLD, deodorant: HOUSEHOLD, 'bin bag': HOUSEHOLD,
  'trash bag': HOUSEHOLD, sponge: HOUSEHOLD, bleach: HOUSEHOLD, diaper: HOUSEHOLD, nappy: HOUSEHOLD,
  foil: HOUSEHOLD, 'cling film': HOUSEHOLD, 'cling wrap': HOUSEHOLD, 'plastic wrap': HOUSEHOLD,
  pasta: PANTRY, spaghetti: PANTRY, noodle: PANTRY, rice: PANTRY, flour: PANTRY, sugar: PANTRY,
  oil: PANTRY, 'olive oil': PANTRY, sauce: PANTRY, cereal: PANTRY, oat: PANTRY, 'rolled oat': PANTRY,
  granola: PANTRY, barley: PANTRY,
  salt: PANTRY, vinegar: PANTRY, mustard: PANTRY, ketchup: PANTRY, mayonnaise: PANTRY, mayo: PANTRY,
  honey: PANTRY, jam: PANTRY, 'peanut butter': PANTRY, nut: PANTRY, almond: PANTRY,
  raisin: PANTRY, apricot: PRODUCE, chickpea: PANTRY, lentil: PANTRY,
  // "pepper" on an English list is nearly always the black kind; the vegetable
  // is listed by colour above. Same stem as Swedish "peppar", same shelf.
  bean: PANTRY, pepper: PANTRY, 'black pepper': PANTRY, spice: PANTRY, cumin: PANTRY,
  // Swedish "paprika" is the vegetable, English "paprika" the spice — the
  // spice spellings are longer, so they win.
  paprika: PRODUCE, 'smoked paprika': PANTRY, 'paprika powder': PANTRY,
  'chilli flake': PANTRY, 'chili flake': PANTRY, 'chilli powder': PANTRY, 'chili powder': PANTRY,
  cinnamon: PANTRY, curry: PANTRY, 'garam masala': PANTRY, stock: PANTRY, broth: PANTRY,
  'coconut milk': PANTRY, 'tomato paste': PANTRY, 'tomato puree': PANTRY, passata: PANTRY,
  'tinned tomato': PANTRY, 'canned tomato': PANTRY, 'tin of tomato': PANTRY, 'can of tomato': PANTRY,
  'soy sauce': PANTRY,
  'baking powder': PANTRY, 'baking soda': PANTRY, yeast: PANTRY, vanilla: PANTRY, chocolate: PANTRY,
  cracker: PANTRY, crisp: PANTRY, chip: PANTRY, biscuit: PANTRY, cookie: PANTRY, popcorn: PANTRY,
  couscous: PANTRY, bulgur: PANTRY, quinoa: PANTRY,
  // Canonical spellings from synonyms.ts. nameKey turns "köttbullar" into
  // "meatball" and "gräslök" into "chives" before this map ever sees them, so
  // the Swedish substrings ("kött", "lök") that used to place those no longer
  // can — the English word has to be here instead.
  pea: PRODUCE, beetroot: PRODUCE, radish: PRODUCE, asparagus: PRODUCE, fennel: PRODUCE,
  pumpkin: PRODUCE, cherry: PRODUCE, chives: PRODUCE, 'brussels sprout': PRODUCE,
  'bay leaf': PANTRY, cardamom: PANTRY, clove: PANTRY, saffron: PANTRY, caraway: PANTRY,
  allspice: PANTRY, cocoa: PANTRY, syrup: PANTRY,
  'ground meat': DAIRY, meatball: DAIRY,

  // Swedish keywords. "vinäger" beats "vin" (both Pantry) by length.
  mjölk: DAIRY, grädde: DAIRY, gräddfil: DAIRY, filmjölk: DAIRY,
  kvarg: DAIRY, ost: DAIRY, smör: DAIRY,
  halloumi: DAIRY, grillost: DAIRY, fetaost: DAIRY, keso: DAIRY,
  parmesan: DAIRY, mozzarella: DAIRY, cheddar: DAIRY,
  crème: DAIRY, mascarpone: DAIRY, ricotta: DAIRY, ägg: DAIRY,
  tomat: PRODUCE, lök: PRODUCE, vitlök: PRODUCE, purjolök: PRODUCE,
  salladslök: PRODUCE, potatis: PRODUCE, morot: PRODUCE, morötter: PRODUCE,
  spenat: PRODUCE, sallad: PRODUCE, gurka: PRODUCE,
  äpple: PRODUCE, banan: PRODUCE, citron: PRODUCE,
  ingefära: PRODUCE, avokado: PRODUCE, blomkål: PRODUCE,
  champinjon: PRODUCE, svamp: PRODUCE, persilja: PRODUCE,
  basilika: PRODUCE, koriander: PRODUCE, chili: PRODUCE, vitkål: PRODUCE,
  grönsak: PRODUCE, druvor: PRODUCE, päron: PRODUCE,
  kyckling: DAIRY, nötkött: DAIRY, fläsk: DAIRY, gris: DAIRY, färs: DAIRY,
  korv: DAIRY, skinka: DAIRY, lax: DAIRY, fisk: DAIRY,
  räkor: DAIRY, kött: DAIRY, biff: DAIRY, kalkon: DAIRY, tonfisk: DAIRY,
  // Words that would otherwise be swallowed by a shorter keyword they contain
  // ("ostron" starts with "ost"). Listed explicitly so longest-first wins.
  ostron: DAIRY, musslor: DAIRY, ostbågar: PANTRY, limpa: BAKERY, leverpastej: DAIRY,
  bröd: BAKERY, bulle: BAKERY, fralla: BAKERY,
  tortilla: BAKERY, tunnbröd: BAKERY,
  glass: FROZEN, fryst: FROZEN, frysta: FROZEN,
  spagetti: PANTRY, ris: PANTRY, mjöl: PANTRY, socker: PANTRY,
  olja: PANTRY, olivolja: PANTRY, buljong: PANTRY, tomatpuré: PANTRY,
  kokosmjölk: PANTRY, linser: PANTRY, bönor: PANTRY,
  peppar: PANTRY, krydd: PANTRY, vinäger: PANTRY,
  senap: PANTRY, honung: PANTRY, nötter: PANTRY,
  havregryn: PANTRY, kanel: PANTRY, nudlar: PANTRY, majonäs: PANTRY, majonnäs: PANTRY,
  miso: PANTRY, nori: PANTRY,
  // Tinned/dried forms of a fresh ingredient belong in the pantry, so they need
  // to be longer keys than the fresh word they contain.
  'krossade tomater': PANTRY, paprikapulver: PANTRY, vitlökspulver: PANTRY,
  chilipulver: PANTRY, lökpulver: PANTRY, currypulver: PANTRY,
  'torkad oregano': PANTRY, jäst: PANTRY, bakpulver: PANTRY,
  läsk: PANTRY, kaffe: PANTRY,
  vin: PANTRY, saft: PANTRY, vatten: PANTRY,
  tvål: HOUSEHOLD, schampo: HOUSEHOLD, tvättmedel: HOUSEHOLD,
  hushållspapper: HOUSEHOLD, toalettpapper: HOUSEHOLD, diskmedel: HOUSEHOLD,
}

// Keywords normalised the same way names are, sorted longest-first once at module
// load. Longest-first makes a specific word beat a shorter one it contains
// ("ice cream" → Frozen before "cream" → Dairy; "vinäger" before "vin").
// Both sides go through nameKey, so "Mjölken"/"mjolk"/"MJÖLK" all match `mjölk`.
export const KEYWORDS: { word: string; key: string; category: string }[] = Object.entries(MAP)
  .map(([word, category]) => ({ word, key: nameKey(word), category }))
  .filter(k => k.key.length > 0)
  .sort((a, b) => b.key.length - a.key.length)

// The same words only folded (case and accents), for the compounds nameKey can't
// see into: it swaps a Swedish word for its English synonym ("vitlök" →
// "garlic"), so "vitlöksklyftor" no longer contains the key. Longest-first too,
// and five letters at least: a short English word ("tea", "oil") would turn up
// inside unrelated Swedish ones.
const FOLDED_KEYWORDS = Object.entries(MAP)
  .map(([word, category]) => ({ key: fold(word), category }))
  .filter(k => k.key.length >= 5)
  .sort((a, b) => b.key.length - a.key.length)

// A "frozen" qualifier beats whatever the ingredient itself is. Stemmed like
// the names it is compared to — "frozen" stems to "froz", so a raw set never
// matched it.
const FROZEN_MARKERS = new Set(['fryst', 'frysta', 'djupfryst', 'djupfrysta', 'frozen'].map(stemToken))

/**
 * Best-guess category *name* for an item, or null when nothing matches.
 *
 * Matching is substring-on-the-normalised-name rather than whole-word: Swedish
 * compounds mean the ingredient word is often glued to a qualifier
 * ("kycklingfilé" → kyckling, "rödvinsvinäger" → vinäger).
 */
export function suggestCategory(name: string): string | null {
  const key = nameKey(name)
  if (!key) return null
  // "fryst spenat" is bought in the freezer aisle, not with the vegetables —
  // and "spenat" is the longer word, so this has to win before the main loop.
  if (tokens(key).some(t => FROZEN_MARKERS.has(t))) return FROZEN
  for (const { key: keyword, category } of KEYWORDS) {
    if (key.includes(keyword)) return category
  }
  const folded = fold(name)
  for (const { key: keyword, category } of FOLDED_KEYWORDS) {
    if (folded.includes(keyword)) return category
  }
  return null
}

/**
 * Turns a suggested category name into a real category id for this household.
 *
 * Households can rename their seeded categories ("Skafferi" → "Torrvaror")
 * but keep the seeded icon, so we try the name — in any language it was ever
 * seeded as — first and fall back to the icon. Matching on the name alone is
 * what used to drop every imported item into "Other" for anyone who had
 * renamed a category.
 */
export function categoryIdForSuggestion(
  canonical: string | null,
  categories: Category[],
): string | null {
  if (!canonical) return null
  const slot = SLOTS.find(s => s.name === canonical)
  const names = new Set([fold(canonical), ...(slot?.aliases ?? [])])
  const byName = categories.find(c => names.has(fold(c.name)))
  if (byName) return byName.id
  const byIcon = slot ? categories.find(c => c.icon === slot.icon) : undefined
  return byIcon?.id ?? null
}

/**
 * nameKey → the category the household most recently filed that name under.
 *
 * Fed with both the current list and past (cleared) items; later `updated_at`
 * wins, so correcting an item's category once teaches every future import.
 * Items with no category contribute nothing.
 */
export function buildCategoryMemory(items: Pick<Item, 'name' | 'category_id' | 'updated_at'>[]): Map<string, string> {
  const best = new Map<string, { categoryId: string; at: string }>()
  for (const item of items) {
    if (!item.category_id) continue
    const key = nameKey(item.name)
    if (!key) continue
    const prev = best.get(key)
    const at = item.updated_at ?? ''
    if (!prev || at > prev.at) best.set(key, { categoryId: item.category_id, at })
  }
  return new Map([...best].map(([key, v]) => [key, v.categoryId]))
}

const MIN_PREFIX = 4

/**
 * The layered resolver the importer uses per ingredient:
 *   1. the household filed this exact name before
 *   2. …or filed something this name starts with ("tomatpuré" ← "tomat")
 *   3. …otherwise the keyword map
 */
export function resolveCategoryId(
  name: string,
  memory: Map<string, string>,
  categories: Category[],
): string | null {
  const key = nameKey(name)
  // The memory comes from cached history, so it can still hold a category
  // deleted since; only the household's current categories are an answer.
  const exists = (id: string) => categories.some(c => c.id === id)
  if (key) {
    const exact = memory.get(key)
    if (exact && exists(exact)) return exact
    // "potatis fast" filed before, "fast potatis" now.
    for (const [k, id] of memory) if (sameWords(k, key) && exists(id)) return id

    // Longest remembered key that this name starts with (or that starts with
    // this name) — the more specific memory wins. Both sides need a few
    // characters so "ris" doesn't inherit "riskakor".
    if (key.length >= MIN_PREFIX) {
      let bestKey = ''
      let bestId: string | null = null
      for (const [k, id] of memory) {
        if (k.length < MIN_PREFIX || k.length <= bestKey.length || !exists(id)) continue
        if (key.startsWith(k) || k.startsWith(key)) { bestKey = k; bestId = id }
      }
      if (bestId) return bestId
    }
  }
  return categoryIdForSuggestion(suggestCategory(name), categories)
}

/**
 * Which seeded slot (0–5, the order of SEED_CATEGORIES) a household's category
 * is, by its current or legacy name or by its seeded icon; null for a category
 * the household made itself. lib/categoryTint.ts colours by this, and the watch
 * repeats the same rule in watch/source/Theme.mc.
 */
export function slotIndexOf(category: Pick<Category, 'name' | 'icon'>): number | null {
  const name = fold(category.name)
  const byName = SLOTS.findIndex(s => fold(s.name) === name || s.aliases.includes(name))
  if (byName >= 0) return byName
  const byIcon = category.icon ? SLOTS.findIndex(s => s.icon === category.icon) : -1
  return byIcon >= 0 ? byIcon : null
}

/** The quick-add pill's override: the next category in list order, wrapping. */
export function nextCategory(ids: string[], current: string | null): string | null {
  if (!ids.length) return null
  return ids[(current === null ? 0 : ids.indexOf(current) + 1) % ids.length]
}
